import type { CanonicalEntry } from "../segmenting/canonical-entry.js";
import { Glosses } from "../segmenting/glosses.js";
import type { SegmentedWord } from "../segmenting/segmented-words.js";

/**
 * What each word in the list means, fetched after the list is already drawn.
 *
 * A row used to say how a word is written, how it is read and how hard it is,
 * and left the reader to follow a link for what it means. The meaning is what
 * most people paste a paragraph in for, and it is 420 bytes behind the edge
 * cache at `/cidian/<word>/canonical.json`.
 *
 * **Nothing waits for it.** The list is drawn from the dictionary the page
 * already holds, and a meaning lands in its row whenever it arrives.
 * `SegmenterView.showGloss` writes one cell rather than redrawing the table,
 * so three hundred answers arriving one at a time cost three hundred small
 * writes instead of three hundred full draws.
 *
 * **`Glosses` is shared with the extension's panel** and carries the caching.
 * A word this page has already asked about is answered from memory, and a word
 * this device was told about within the day is answered from the Cache Storage
 * store. See `src/segmenting/glosses.ts`.
 *
 * **Only the words the 词典 publishes are asked about.** The address index the
 * tool already fetched says which those are, so a text full of names costs no
 * 404s. See `cidian-index.ts`.
 */

/** How many meanings a closed row shows. */
export const shownMeanings = 3;

/**
 * How many entries are in flight at once.
 *
 * A pasted article holds a few hundred distinct words and the site is behind
 * HTTP/2, so asking for all of them together is allowed and would still put a
 * few hundred streams in front of anything else the page wants. Six at a time
 * in the order the list is shown fills the top of the table first, which is
 * the part a reader is looking at.
 *
 * **The six belong to the tool rather than to one reading of the text**, and
 * that is what the queue below is for. A reader typing into the box is read
 * again every few hundred milliseconds, so a limit held per reading is no
 * limit at all: on a slow connection, where it matters most, thirty readings
 * with a request outstanding apiece would be a hundred and eighty streams.
 */
const askedAtOnce = 6;

/** The meanings a closed row shows, joined for one line. */
export function shortGloss(entry: CanonicalEntry): string {
  return entry.meanings.slice(0, shownMeanings).join("; ");
}

/** Every meaning the entry carries, for a row a reader has opened. */
export function wholeGloss(entry: CanonicalEntry): string {
  return entry.meanings.join("; ");
}

/** What this needs told, both of which a test hands its own. */
export interface SegmenterGlossesProperties {
  /** Where the answers come from. It has its own default. */
  readonly glosses?: Glosses | undefined;

  /** Called with a headword and its entry once the site has answered. */
  readonly onEntry: (headword: string, entry: CanonicalEntry) => void;
}

/** The 词典 entries behind the words in the current segmentation. */
export class SegmenterGlosses {
  readonly #glosses: Glosses;
  readonly #onEntry: (headword: string, entry: CanonicalEntry) => void;
  readonly #known = new Map<string, CanonicalEntry>();

  /**
   * The words waiting for a slot, in the order the list gathered them.
   *
   * Replaced rather than added to on every reading of the text. A reader who
   * has typed on is looking at a different list, and the words of the one they
   * left should not be holding slots in front of it.
   */
  #queued: readonly string[] = [];

  /** The words a slot is currently spent on. Never more than `askedAtOnce`. */
  readonly #asking = new Set<string>();

  constructor(properties: SegmenterGlossesProperties) {
    this.#glosses =
      properties.glosses ??
      new Glosses({ siteUrl: "", fetch: globalThis.fetch });
    this.#onEntry = properties.onEntry;
  }

  /** What this page can already say about a word, for a row being drawn. */
  known(headword: string): CanonicalEntry | undefined {
    return this.#known.get(headword);
  }

  /**
   * Ask about every word in this segmentation the 词典 has a page for.
   *
   * The words are asked about in the order they are given, which is the order
   * the list was gathered in rather than the order it is shown in. Reordering
   * the table asks for nothing, every answer already being on its way.
   *
   * **A request already out is left to finish**, where the queue behind it is
   * thrown away. What comes back is a fact about a word rather than about a
   * text, so it is worth keeping whichever reading asked for it, and a word no
   * longer on the page has no cell for `showGloss` to write to.
   */
  read(words: readonly SegmentedWord[]): void {
    this.#queued = this.#unanswered(words);
    this.#fill();
  }

  /** The words with a page this page has neither been told nor asked about. */
  #unanswered(words: readonly SegmentedWord[]): readonly string[] {
    const wanted = new Set<string>();

    for (const word of words) {
      if (
        word.address !== undefined &&
        word.headword !== undefined &&
        !this.#known.has(word.headword) &&
        !this.#asking.has(word.headword)
      ) {
        wanted.add(word.headword);
      }
    }

    return [...wanted];
  }

  /** Spend every free slot on the front of the queue. */
  #fill(): void {
    const taking = this.#queued.slice(0, askedAtOnce - this.#asking.size);

    this.#queued = this.#queued.slice(taking.length);

    for (const word of taking) {
      this.#asking.add(word);
      void this.#found(word);
    }
  }

  /**
   * Ask about one word, and tell the page where there was something to say.
   *
   * A request that will not complete is swallowed. The row keeps the empty
   * cell it was drawn with, which is the table exactly as it was before any of
   * this existed.
   *
   * The slot is given back either way, and the next word takes it. A failure
   * that kept its slot would leave the rest of the list unasked.
   */
  async #found(headword: string): Promise<void> {
    const entry = await this.#glosses.of(headword).catch(() => undefined);

    this.#asking.delete(headword);

    if (entry !== undefined) {
      this.#known.set(headword, entry);
      this.#onEntry(headword, entry);
    }

    this.#fill();
  }
}
